<script setup lang="ts">
import { type IMultiselectOption, MultiSelect } from '~/components/ui/multiselect'
import { LucideCircleX, LucideNewspaper, LucideSearch } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { useMeiliSearch } from '#imports'
import { useDebounce } from '@vueuse/core'
import type { SearchResponse } from 'meilisearch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import type { ILocation, IResponseData, MeiliSearchOrganizationHit } from '~/types'
import {
  EntityBranchCard,
  EntityBranchCardSkeleton,
  type IBranchCard,
  type MeiliSearchBranchHit,
  serviceLabels,
  branchTypeLabels,
  organizationTypeLabels
} from '~/components/entity/branch'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { WidgetMap } from '~/components/widget/map'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'

const { t, locale } = useI18n()
const route = useRoute()

const { getPathWithLocale, getPathWithoutLocale } = useLocalizedPath()
const { getTypeLabels } = useEntityBranch()

// Route parameter to organization type mapping
const routeParamToOrgType: Record<string, string> = {
  bank: 'bank',
  microcredit: 'microcredit_company',
  insurance: 'insurance_company',
  'money-transfer': 'money_transfer',
  'exchange-office': 'exchange_office',
  'crypto-company': 'crypto_company'
}

// Breadcrumbs - based only on route parameters, not dynamic selection
const breadcrumbs = computed(() => {
  const links = []

  // Check route parameters for organization context
  for (const [paramKey, orgType] of Object.entries(routeParamToOrgType)) {
    const paramValue = route.params[paramKey]
    if (paramValue) {
      // Add organization type section (e.g., "Банки Кыргызстана")
      const orgTypeLabel = t(organizationTypeLabels[orgType])
      if (orgTypeLabel) {
        const sectionUrl =
          orgType === 'bank' ? '/banks' : orgType === 'microcredit_company' ? '/microcredits' : null

        links.push({
          title: `${orgTypeLabel}`,
          url: sectionUrl
        })
      }

      // Add specific organization from route
      if (organizationsOptions.value?.length) {
        const selectedOrg = organizationsOptions.value.find((org) => org.slug === paramValue)
        if (selectedOrg) {
          const orgUrl =
            orgType === 'bank'
              ? `/bank/${selectedOrg.slug}`
              : orgType === 'microcredit_company'
                ? `/microcredit/${selectedOrg.slug}`
                : null

          links.push({
            title: selectedOrg.label,
            url: orgUrl
          })
        }
      }
      break
    }
  }

  // Add final breadcrumb
  links.push({ title: t('page.map.breadcrumb.branches_map') })

  return links
})

// Get organization name for SEO content
const organizationName = computed(() => {
  // Check route parameters for organization context
  for (const paramKey of Object.keys(routeParamToOrgType)) {
    const paramValue = route.params[paramKey]
    if (paramValue && organizationsOptions.value?.length) {
      const selectedOrg = organizationsOptions.value.find((org) => org.slug === paramValue)
      if (selectedOrg) {
        return selectedOrg.label
      }
    }
  }
  return ''
})

// organization types used for map page
const allOrganizationsTypes = computed<IMultiselectOption[]>(() => [
  { label: t('common.banks'), value: 'bank', id: 1 },
  {
    label: t('common.microcredit_companies'),
    value: 'microcredit_company',
    id: 2
  },
  {
    label: t('common.insurance_companies'),
    value: 'insurance_company',
    id: 3
  },
  { label: t('common.money_transfers'), value: 'money_transfer', id: 4 },
  { label: t('common.exchange_offices'), value: 'exchange_office', id: 5 },
  { label: t('common.crypto'), value: 'crypto_company', id: 6 }
])

const activeBranchId = ref<number | null>(null)

// Organizations selection

const selectedOrganizationType = ref<string>('')
const selectedOrganizations = ref<IMultiselectOption[]>([])
const organizationsResult = ref<SearchResponse<MeiliSearchOrganizationHit> | null>(null)
const isLoadingOrganizations = ref(false)
const firstLoadDone = ref(false)
const filtersInitialized = ref(false)

// Helper function to build query params from current selections
const buildQueryParams = (): Record<string, string> => {
  const queryParams: Record<string, string> = {}

  if (selectedOrganizationType.value) {
    queryParams.t = selectedOrganizationType.value
  }

  if (selectedLocationId.value) {
    queryParams.l = selectedLocationId.value.toString()
  }

  if (selectedOrganizations.value.length > 0) {
    queryParams.o = selectedOrganizations.value.map((org) => org.value).join(',')
  }

  if (selectedServicesOptions.value.length > 0) {
    queryParams.b = selectedServicesOptions.value.map((s) => s.value).join(',')
  }

  if (selectedServices.value.length > 0) {
    queryParams.s = selectedServices.value.map((s) => s.value).join(',')
  }

  if (searchQueryString.value) {
    queryParams.q = searchQueryString.value
  }

  return queryParams
}

// Helper function to handle navigation to map
const navigateToMapIfNeeded = () => {
  // Navigate to map page if not already there
  if (firstLoadDone.value && getPathWithoutLocale(route.path) !== '/map') {
    navigateTo({
      path: getPathWithLocale('/map'),
      query: buildQueryParams()
    })
  }
}

// Function to update URL query parameters when on map page or organization pages
const updateUrlQueryParams = () => {
  if (firstLoadDone.value) {
    if (getPathWithoutLocale(route.path) === '/map') {
      // Full query params for map page
      const queryParams = buildQueryParams()

      // Only update if query params have actually changed
      const currentQuery = route.query
      const hasChanged =
        Object.keys(queryParams).some((key) => queryParams[key] !== currentQuery[key]) ||
        Object.keys(currentQuery).some((key) => !queryParams[key] && currentQuery[key])

      if (hasChanged) {
        navigateTo(
          {
            path: getPathWithLocale('/map'),
            query: queryParams
          },
          { replace: true }
        )
      }
    } else if (getPathWithoutLocale(route.path) !== '/map') {
      // Limited query params for organization pages (only b, s, q)
      const organizationPageParams: Record<string, string> = {}

      if (selectedServicesOptions.value.length > 0) {
        organizationPageParams.b = selectedServicesOptions.value.map((s) => s.value).join(',')
      }

      if (selectedServices.value.length > 0) {
        organizationPageParams.s = selectedServices.value.map((s) => s.value).join(',')
      }

      if (searchQueryString.value) {
        organizationPageParams.q = searchQueryString.value
      }

      // Only update if query params have actually changed
      const currentQuery = route.query
      const hasChanged =
        Object.keys(organizationPageParams).some(
          (key) => organizationPageParams[key] !== currentQuery[key]
        ) ||
        Object.keys(currentQuery).some(
          (key) =>
            !organizationPageParams[key] && currentQuery[key] && ['b', 's', 'q'].includes(key)
        )

      if (hasChanged) {
        //no need to wrap in locale helper as route.path has locale already
        navigateTo(
          {
            path: route.path,
            query: organizationPageParams
          },
          { replace: true }
        )
      }
    }
  }
}

const organizationsOptions = computed(() => {
  if (!organizationsResult.value?.hits) return []

  return organizationsResult.value.hits.map((org) => ({
    label: org.title[locale.value],
    value: org.id.toString(),
    id: org.id,
    slug: org.slug
  }))
})

const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl

// Get organization data for SEO (SSR-compatible)
const getOrganizationSlug = () => {
  for (const paramKey of Object.keys(routeParamToOrgType)) {
    const paramValue = route.params[paramKey]
    if (paramValue) {
      return paramValue as string
    }
  }
  return null
}

const getOrganizationType = () => {
  for (const [paramKey, orgType] of Object.entries(routeParamToOrgType)) {
    const paramValue = route.params[paramKey]
    if (paramValue) {
      return orgType
    }
  }
  return null
}

const organizationSlug = getOrganizationSlug()
const organizationType = getOrganizationType()

// Map organization type to API endpoint
const getApiEndpoint = (orgType: string) => {
  const endpointMap: Record<string, string> = {
    bank: 'banks',
    microcredit_company: 'microcredit',
    insurance_company: 'insurance',
    money_transfer: 'money-transfers',
    exchange_office: 'exchange-offices',
    crypto_company: 'crypto-companies'
  }
  return endpointMap[orgType]
}

// Fetch organization data for SEO if we have slug and type
const apiEndpoint = organizationType ? getApiEndpoint(organizationType) : null
const { data: organizationData } =
  organizationSlug && apiEndpoint
    ? await useApi(`/${apiEndpoint}/${organizationSlug}`, {
        key: `map-seo-${organizationType}-${organizationSlug}`,
        server: true
      })
    : { data: ref(null) }

// Create organization entity for useSeoFromEntity
const organizationEntity = computed(() => {
  if (organizationData.value?.data) {
    return {
      title: organizationData.value.data.title,
      url: route.path,
      organization: {
        title: organizationData.value.data.title
      }
    }
  }
  return null
})

// Use SEO from entity when organization is available
if (organizationEntity.value) {
  useSeoFromEntity(organizationEntity, baseUrl, locale.value)
}

// Map organization type to MeiliSearch index
const getOrganizationIndex = (type: string) => {
  const indexMap: Record<string, string> = {
    bank: 'banks',
    microcredit_company: 'microcredit_companies',
    insurance_company: 'insurance_companies',
    money_transfer: 'money_transfers',
    exchange_office: 'exchange_offices',
    crypto_company: 'crypto_companies'
  }
  return indexMap[type] || 'banks'
}

const fetchOrganizations = async () => {
  if (!selectedOrganizationType.value) {
    organizationsResult.value = null
    selectedOrganizations.value = []
    return
  }

  try {
    const index = getOrganizationIndex(selectedOrganizationType.value)
    const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_${index}`)
    isLoadingOrganizations.value = true

    organizationsResult.value = await search('', {
      hitsPerPage: 1000,
      page: 1,
      sort: ['weight:desc'],
      attributesToSearchOn: ['title']
    })
  } catch (e) {
    console.error('Organizations search error:', e)
    organizationsResult.value = null
  } finally {
    isLoadingOrganizations.value = false
  }
}

// Watch organization type changes to fetch organizations
watch(
  selectedOrganizationType,
  (newValue, oldValue) => {
    // Only clear organizations when actually switching organization types
    if (oldValue !== undefined && newValue !== oldValue) {
      selectedOrganizations.value = []
    }
    fetchOrganizations()
  },
  { immediate: true }
)

// Location selection
const apiParams = {
  per_page: 1000
}

const { data: locationsData, pending: isLoadingLocations } = useApi<IResponseData<ILocation[]>>(
  () => useUrlWithParams('/locations', apiParams),
  {
    key: 'map-page-locations'
  }
)

const locations = computed(() => locationsData.value?.data)
const selectedLocationId = ref<number | undefined>(undefined)

// Branch types computed from branchTypeLabels
const branchTypes = computed(() =>
  Object.entries(branchTypeLabels).map(([value, label], index) => ({
    label: t(label),
    value,
    id: index + 1
  }))
)

const selectedServicesOptions = ref<IMultiselectOption[]>([])

// Services options computed from serviceLabels
const servicesOptions = computed(() =>
  Object.entries(serviceLabels).map(([value, label], index) => ({
    label: t(label),
    value,
    id: index + 1
  }))
)

const selectedServices = ref<IMultiselectOption[]>([])

// Set first location as default when locations are loaded (only if no query params)
watchEffect(() => {
  if (locations.value?.length && !selectedLocationId.value && !route.query.l) {
    const bishkekLocation = locations.value.find((location) => location.slug === 'bishkek')
    selectedLocationId.value = bishkekLocation?.id || locations.value[0].id
  }
})

// Handle route parameters for organization selection (one-time setup)
const initializeFromRoute = () => {
  // Only initialize once, not after user interactions
  if (firstLoadDone.value) return

  // First, set organization type based on route params
  let foundOrgType = false
  for (const [paramKey, orgType] of Object.entries(routeParamToOrgType)) {
    if (route.params[paramKey]) {
      selectedOrganizationType.value = orgType
      foundOrgType = true
      break
    }
  }
  // Default to 'bank' if no organization type found in route and no query params
  if (!foundOrgType && !route.query.t) {
    selectedOrganizationType.value = 'bank'
  }

  // Then, if we have query params, initialize from them (but keep organization from route params)
  if (route.query && Object.keys(route.query).length > 0) {
    initializeFromQueryParams()
  }

  // Organizations will be selected when options are loaded via watch
}

// Initialize from query params (after redirect)
const initializeFromQueryParams = () => {
  // Set organization type from query params (has priority over default)
  if (route.query.t) {
    selectedOrganizationType.value = route.query.t as string
  }

  // Set location from query params (has priority)
  if (route.query.l) {
    selectedLocationId.value = parseInt(route.query.l as string)
  }

  // Set search query
  if (route.query.q) {
    searchQueryString.value = route.query.q as string
  } else {
    searchQueryString.value = ''
  }

  // Set organizations (after options are loaded) only if not from route params
  const hasRouteParams = route.params.bank || route.params.microcredit
  if (route.query.o && organizationsOptions.value?.length && !hasRouteParams) {
    const orgIds = (route.query.o as string).split(',')
    selectedOrganizations.value = organizationsOptions.value.filter((org) =>
      orgIds.includes(org.value)
    )
  } else if (!hasRouteParams) {
    selectedOrganizations.value = []
  }

  // Set branch types
  if (route.query.b) {
    const branchTypeValues = (route.query.b as string).split(',')
    selectedServicesOptions.value = branchTypes.value.filter((bt) =>
      branchTypeValues.includes(bt.value)
    )
  } else {
    selectedServicesOptions.value = []
  }

  // Set services
  if (route.query.s) {
    const serviceValues = (route.query.s as string).split(',')
    selectedServices.value = servicesOptions.value.filter((s) => serviceValues.includes(s.value))
  } else {
    selectedServices.value = []
  }
}

// Initialize from route when organizations are loaded
watch(
  organizationsOptions,
  () => {
    if (organizationsOptions.value?.length && !firstLoadDone.value) {
      let organizationSelected = false

      // First priority: Select specific organization from URL params (for routes like /bank/bakai/branches)
      for (const [paramKey, orgType] of Object.entries(routeParamToOrgType)) {
        const paramValue = route.params[paramKey]
        if (
          paramValue &&
          selectedOrganizationType.value === orgType &&
          !selectedOrganizations.value.some((org) => org.slug === paramValue)
        ) {
          const selectedOrg = organizationsOptions.value.find((org) => org.slug === paramValue)
          if (selectedOrg) {
            selectedOrganizations.value = [selectedOrg]
            organizationSelected = true

            // After selecting organization, set branch type based on route
            nextTick(() => {
              // Auto-select branch type based on route only if no query params override
              if (!route.query.b) {
                const routeToBranchType: Record<string, string> = {
                  'bank-branches': 'branch',
                  'microcredit-branches': 'branch',
                  'bank-atms': 'atm',
                  'bank-terminals': 'terminal'
                }
                const targetBranchType = routeToBranchType[useCleanRouteName(route.name) as string]
                if (targetBranchType) {
                  const branchOption = branchTypes.value.find(
                    (type) => type.value === targetBranchType
                  )
                  if (branchOption) {
                    selectedServicesOptions.value = [branchOption]
                  }
                }
              }
            })
            break
          }
        }
      }

      // Second priority: Select organizations from query params (for direct /map visits with query params)
      if (!organizationSelected && route.query.o) {
        const orgIds = (route.query.o as string).split(',')
        selectedOrganizations.value = organizationsOptions.value.filter((org) =>
          orgIds.includes(org.value)
        )
      }

      // Mark filters as initialized after organization selection is complete, then fetch
      if (!filtersInitialized.value) {
        nextTick(() => {
          nextTick(() => {
            filtersInitialized.value = true
            if (selectedLocationId.value) {
              fetchBranches()
            }
          })
        })
      }
    }
  },
  { immediate: true }
)

// Initialize branch types and services from query params when they become available
watch(
  [branchTypes, servicesOptions],
  () => {
    if (branchTypes.value.length > 0 && servicesOptions.value.length > 0 && !firstLoadDone.value) {
      // Set branch types from query params
      if (route.query.b) {
        const branchTypeValues = (route.query.b as string).split(',')
        selectedServicesOptions.value = branchTypes.value.filter((bt) =>
          branchTypeValues.includes(bt.value)
        )
      }

      // Set services from query params
      if (route.query.s) {
        const serviceValues = (route.query.s as string).split(',')
        selectedServices.value = servicesOptions.value.filter((s) =>
          serviceValues.includes(s.value)
        )
      }
    }
  },
  { immediate: true }
)

onMounted(() => {
  // Initialize from route immediately
  initializeFromRoute()

  // Set default branch type only if not on specific branch/ATM routes and no query parameters
  nextTick(() => {
    if (branchTypes.value.length > 0 && useCleanRouteName(route.name) === 'map' && !route.query.b) {
      selectedServicesOptions.value = [branchTypes.value[0]]
    }

    // For routes with organization params (e.g. /bank/bakai/atms), defer initialization
    // to the organizationsOptions watcher which sets filters and filtersInitialized itself.
    // For plain /map with no route params, initialize immediately.
    const hasOrgRouteParams = Object.keys(routeParamToOrgType).some((k) => route.params[k])
    if (!hasOrgRouteParams) {
      nextTick(() => {
        filtersInitialized.value = true

        if (selectedLocationId.value) {
          fetchBranches()
        }
      })
    }

    // Mark page as fully loaded after all initialization is complete
    setTimeout(() => {
      firstLoadDone.value = true
    }, 1000)
  })
})

// Search functionality
const searchQueryString = ref('')
const debouncedQuery = useDebounce(searchQueryString, 500)

const result = ref<SearchResponse<MeiliSearchBranchHit> | null>(null)
const isSearching = ref(false)
const hasError = ref(false)
const isInitialLoad = ref(true)
const lastSearchParams = ref<string>('')

// Active filter for MeiliSearch
const activeFilter = computed(() => {
  const filters: string[] = []

  // Organization type filter
  if (selectedOrganizationType.value) {
    filters.push(`organization.type = "${selectedOrganizationType.value}"`)
  }

  // Selected organizations filter
  if (selectedOrganizations.value.length) {
    const orgIds = selectedOrganizations.value.map((org) => `organization.id = ${org.value}`)
    filters.push(`(${orgIds.join(' OR ')})`)
  }

  // Branch types filters
  if (selectedServicesOptions.value.length) {
    const serviceFilters = selectedServicesOptions.value.map((s) => `types.${s.value} = true`)
    filters.push(`(${serviceFilters.join(' OR ')})`)
  }

  // Services filters
  if (selectedServices.value.length) {
    const serviceFilters = selectedServices.value.map((s) => `services.${s.value} = true`)
    filters.push(`(${serviceFilters.join(' OR ')})`)
  }

  // Location filter
  if (selectedLocationId.value) {
    filters.push(`location.id = ${selectedLocationId.value}`)
  }

  return filters.length ? filters : undefined
})

const fetchBranches = async () => {
  // Don't fetch if no location is selected
  if (!selectedLocationId.value) {
    return
  }

  // Create search params signature to avoid duplicate requests
  const searchParamsSignature = JSON.stringify({
    location: selectedLocationId.value,
    orgType: selectedOrganizationType.value,
    orgs: selectedOrganizations.value.map((o) => o.value).sort(),
    branchTypes: selectedServicesOptions.value.map((s) => s.value).sort(),
    services: selectedServices.value.map((s) => s.value).sort(),
    query: debouncedQuery.value,
    filters: activeFilter.value
  })

  // Skip if same request is already being processed or was just made
  if (searchParamsSignature === lastSearchParams.value || isSearching.value) {
    return
  }

  lastSearchParams.value = searchParamsSignature

  try {
    const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_branches`)
    isSearching.value = true

    result.value = await search(debouncedQuery.value, {
      hitsPerPage: 1000,
      page: 1,
      sort: ['updated_at:desc', 'id:desc'],
      attributesToSearchOn: ['title', 'address_formatted', 'organization.title.ru', 'organization.title.ky'],
      attributesToHighlight: ['address_formatted', 'organization.title'],
      highlightPreTag: '<em>',
      highlightPostTag: '</em>',
      ...(activeFilter.value ? { filter: activeFilter.value } : {})
    })

    hasError.value = false
  } catch (e) {
    console.error('Branches search error:', e)
    result.value = null
    hasError.value = true
  } finally {
    isSearching.value = false
    isInitialLoad.value = false
  }
}

// Watch for filter changes (main search trigger)
watch(
  [
    debouncedQuery,
    selectedOrganizations,
    selectedServicesOptions,
    selectedServices,
    selectedLocationId
  ],
  () => {
    // Only fetch if we have a location selected and filters are initialized
    if (selectedLocationId.value && (filtersInitialized.value || firstLoadDone.value)) {
      fetchBranches()
    }
  },
  { deep: true }
)

// Watch for changes that require URL updates or redirects
watch(
  [selectedOrganizations, selectedLocationId, selectedOrganizationType],
  () => {
    if (firstLoadDone.value) {
      if (getPathWithoutLocale(route.path) === '/map') {
        updateUrlQueryParams()
      } else {
        navigateToMapIfNeeded()
      }
    }
  },
  { deep: true }
)

// Watch for changes in filters to update URL query params (no redirect for these)
watch(
  [selectedServicesOptions, selectedServices, debouncedQuery],
  () => {
    if (firstLoadDone.value) {
      updateUrlQueryParams()
    }
  },
  { deep: true }
)

// Watch for query params changes (from external navigation like main menu)
watch(
  () => route.query,
  (newQuery, oldQuery) => {
    if (!firstLoadDone.value) return
    if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return

    initializeFromQueryParams()
  },
  { deep: true }
)

// Map center control
const mapCenter = ref<[number, number]>([74.5698, 42.8746])

// Helper function to generate badge HTML
const getBadgeHtml = (available: boolean, label: string) => {
  const icon = available ? '<span style="color: #2563eb">✓</span>' : '<span>✗</span>'

  return `<span style="${available ? 'border: 1px solid #2563eb; color: #2563eb;' : 'border: 1px solid #eff6ff; color: #374151;'} background: #eff6ff; padding: 4px 8px; border-radius: 12px; font-size: 12px; display: inline-block;">${icon} ${label}</span>`
}

// Helper function to check if coordinates are valid
const hasValidCoordinates = (longitude: string | number, latitude: string | number) => {
  const lon = parseFloat(String(longitude))
  const lat = parseFloat(String(latitude))

  // Check if coordinates are not 0.00000000 or invalid
  return lon !== 0 && lat !== 0 && !isNaN(lon) && !isNaN(lat)
}

// Convert MeiliSearch results to IBranchCard format
const branchCards = computed(() => {
  if (!result.value?.hits) return []

  return result.value.hits.map((hit: MeiliSearchBranchHit): IBranchCard => {
    const types: IBranchCard['types'] = {
      branch: hit.types?.branch || false,
      savings_bank: hit.types?.savings_bank || false,
      atm: hit.types?.atm || false,
      terminal: hit.types?.terminal || false,
      exchange: hit.types?.exchange || false
    }
    const typeLabels = getTypeLabels(types)
    const primaryType = typeLabels.length > 0 ? typeLabels[0] : t('page.map.branch_badges.branch')
    const locationTitle = hit.location?.title[locale.value] || ''
    const highlightedOrgTitle =
      hit._formatted?.organization?.title?.[locale.value] ||
      hit.organization?.title[locale.value] ||
      t('common.organization_label')
    const highlightedTitle = `${primaryType} ${highlightedOrgTitle}${locationTitle ? ` - ${locationTitle}` : ''}`

    return {
      id: hit.id,
      organization_id: hit.organization?.id || 0,
      organization_type: hit.organization?.type || 'bank',
      types,
      services: {
        deposits: hit.services?.deposits || false,
        credits: hit.services?.credits || false,
        micro_credits: hit.services?.micro_credits || false,
        credit_cards: hit.services?.credit_cards || false,
        debit_cards: hit.services?.debit_cards || false,
        insurance: hit.services?.insurance || false,
        partners_atms: hit.services?.partners_atms || false,
        'cash-in': hit.services?.['cash-in'] || false,
        withdrawal: hit.services?.withdrawal || false,
        crypto: hit.services?.crypto || false,
        'all-day': hit.services?.['all-day'] || false
      },
      address_formatted: hit._formatted?.address_formatted || hit.address_formatted || t('page.map.no_address'),
      highlightedTitle,
      location_id: hit.location?.id || 0,
      organization: {
        id: hit.organization?.id || 0,
        type: hit.organization?.type || 'bank',
        title: hit.organization?.title[locale.value] || t('common.organization_label'),
        logo: hit.organization?.logo || ''
      },
      location: {
        id: hit.location?.id || 0,
        slug: hit.location?.slug || '',
        title: locationTitle,
        location_id: hit.location?.location_id || 0
      }
    }
  })
})

// Convert search results to markers data for map
const searchResultsMarkers = computed(() => {
  return branchCards.value
    .map((branch) => {
      const hit = result.value?.hits.find((h) => h.id === branch.id) as MeiliSearchBranchHit

      if (hasValidCoordinates(hit?.longitude, hit?.latitude)) {
        return {
          id: branch.id,
          position: [parseFloat(hit.longitude), parseFloat(hit.latitude)] as [number, number],
          title: branch.organization.title,
          address: branch.address_formatted,
          organizationType: branch.organization.type,
          services: branch.types
        }
      }

      return null
    })
    .filter((marker): marker is NonNullable<typeof marker> => marker !== null)
})

// Use search results from MeiliSearch
const displayMarkers = computed(() => {
  return searchResultsMarkers.value
})

// Convert markers for map component
const mapMarkers = computed(() => {
  return displayMarkers.value.map((marker) => ({
    id: marker.id,
    position: marker.position,
    text: `
      <div class="map-popup_body">
        <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px; padding-right: 10px">
          <h4 style="margin: 0 0 2px 0; font-size: 16px; font-weight: 600; color: #111827;">${marker.title}</h4>
          <p style="margin: 0; font-size: 14px; color: #6b7280; display: flex; align-items: start;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-icon mt-0.5 h-4 w-4 min-w-4 text-gray-400"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
            <span style="display: inline-block; margin-left: 4px;">${marker.address}</span>
          </p>
        </div>

        <div style="display: flex; flex-wrap: wrap; gap: 6px;">
          ${getBadgeHtml(marker.services.branch, t('page.map.branch_badges.branch'))}
          ${getBadgeHtml(marker.services.savings_bank, t('page.map.branch_badges.savings_bank'))}
          ${getBadgeHtml(marker.services.atm, t('page.map.branch_badges.atm'))}
          ${getBadgeHtml(marker.services.terminal, t('page.map.branch_badges.terminal'))}
          ${getBadgeHtml(marker.services.exchange, t('page.map.branch_badges.exchange'))}
        </div>
      </div>
    `
  }))
})

// Check if branch has valid coordinates
const branchHasValidCoordinates = (branch: IBranchCard) => {
  const hit = result.value?.hits.find((h) => h.id === branch.id) as MeiliSearchBranchHit
  return hasValidCoordinates(hit?.longitude, hit?.latitude)
}

// Handle branch card click
const onBranchCardClick = (branch: IBranchCard) => {
  if (branchHasValidCoordinates(branch)) {
    centerMapOnBranch(branch)
  }
}

// Center map on branch and show popup
const centerMapOnBranch = (branch: IBranchCard) => {
  const marker = searchResultsMarkers.value.find((m) => m.id === branch.id)
  if (marker) {
    mapCenter.value = marker.position

    // Trigger popup on map after centering
    nextTick(() => {
      // Find the corresponding marker on the map and trigger click
      const mapComponent = document.querySelector('.map-widget')
      if (mapComponent) {
        // Emit custom event to trigger marker popup
        mapComponent.dispatchEvent(
          new CustomEvent('show-marker-popup', {
            detail: { markerId: branch.id }
          })
        )
      }
    })
  }
}
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <div class="space-y-5" role="main">
        <WidgetSeoContent
          position="upper"
          :variables="{
            organization: organizationName
          }"
        />
        <div class="space-y-5">
          <ClientOnly>
            <div class="space-y-4">
              <div class="flex flex-col gap-2 md:flex-row">
                <div
                  v-if="isLoadingLocations"
                  class="h-[52px] flex-shrink-0 animate-pulse rounded-md bg-gray-200 md:w-[252px]"
                />
                <template v-else>
                  <Select v-model="selectedLocationId">
                    <SelectTrigger
                      :aria-label="$t('page.map.aria_labels.select_city')"
                      variant="searchForm"
                      class="flex-shrink-0 md:w-[252px]"
                    >
                      <SelectValue :placeholder="$t('common.select_city')" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="location in locations"
                        :key="location.id"
                        :value="location.id"
                      >
                        {{ location.title }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </template>
                <Select v-model="selectedOrganizationType">
                  <SelectTrigger
                    :aria-label="$t('page.map.aria_labels.select_organization_type')"
                    variant="searchForm"
                    class="flex-shrink-0 md:w-[252px]"
                  >
                    <SelectValue
                      :placeholder="$t('page.map.placeholders.select_organization_type')"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="option in allOrganizationsTypes"
                      :key="option.id"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <MultiSelect
                  :model-value="selectedOrganizations"
                  :options="organizationsOptions"
                  :placeholder="
                    selectedOrganizationType
                      ? $t('page.map.placeholders.select_organization')
                      : $t('page.map.placeholders.select_organization_first')
                  "
                  :class="[
                    'multiselect_height_52 flex-1',
                    { 'pointer-events-none': !selectedOrganizationType || isLoadingOrganizations }
                  ]"
                  @update:model-value="selectedOrganizations = $event"
                />
              </div>
              <div class="flex flex-col gap-2 md:flex-row">
                <MultiSelect
                  :model-value="selectedServicesOptions"
                  :options="branchTypes"
                  :placeholder="$t('page.map.placeholders.select_branch_type')"
                  class="multiselect_height_52 flex-1"
                  @update:model-value="selectedServicesOptions = $event"
                />
                <MultiSelect
                  :model-value="selectedServices"
                  :options="servicesOptions"
                  :placeholder="$t('page.map.placeholders.select_services')"
                  class="multiselect_height_52 flex-1"
                  @update:model-value="selectedServices = $event"
                />
                <div class="relative min-w-[328px]">
                  <Input
                    v-model="searchQueryString"
                    :placeholder="$t('page.map.placeholders.keyword')"
                    class="h-[52px] pl-14 pr-14"
                  />
                  <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
                  <Button
                    v-if="searchQueryString"
                    size="icon"
                    variant="link"
                    class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
                    @click="searchQueryString = ''"
                  >
                    <LucideCircleX class="size-6" />
                  </Button>
                </div>
              </div>
            </div>
            <template #fallback>
              <div class="space-y-5">
                <div class="flex flex-col gap-2 md:flex-row">
                  <div
                    class="h-[52px] flex-shrink-0 animate-pulse rounded-md bg-gray-200 md:w-[252px]"
                  />
                  <div
                    class="h-[52px] flex-shrink-0 animate-pulse rounded-md bg-gray-200 md:w-[252px]"
                  />
                  <div class="h-[52px] flex-1 animate-pulse rounded-md bg-gray-200" />
                </div>
                <div class="flex flex-col gap-2 md:flex-row">
                  <div class="h-[52px] flex-1 animate-pulse rounded-md bg-gray-200" />
                  <div class="h-[52px] flex-1 animate-pulse rounded-md bg-gray-200" />
                  <div
                    class="h-[52px] flex-shrink-0 animate-pulse rounded-md bg-gray-200 md:w-[328px]"
                  />
                </div>
              </div>
            </template>
          </ClientOnly>
        </div>

        <!-- Map and sidebar layout -->
        <ContentLayout>
          <ContentBlock variant="white">
            <WidgetMap :center="mapCenter" :zoom="13" :markers="mapMarkers" @marker-click="activeBranchId = $event" />
          </ContentBlock>
          <template #right-sidebar>
            <aside>
              <SidebarBlock variant="white">
                <div class="flex h-full flex-col space-y-4">
                  <div class="max-h-[816px] space-y-4 overflow-y-auto" aria-labelledby="branches">
                    <Heading
                      v-if="!isSearching && !isInitialLoad"
                      id="branches"
                      tag="h2"
                      class="sr-only"
                      >{{ $t('common.branches') }}</Heading
                    >
                    <template v-if="isSearching || isInitialLoad">
                      <EntityBranchCardSkeleton v-for="n in 5" :key="n" />
                    </template>

                    <template v-else-if="branchCards && branchCards.length">
                      <div
                        v-for="(branch, i) in branchCards"
                        :key="branch.id"
                        :class="[
                          branchHasValidCoordinates(branch) ? 'cursor-pointer' : 'cursor-default',
                          i === 0 ? '!mt-0' : ''
                        ]"
                        @click="onBranchCardClick(branch)"
                      >
                        <EntityBranchCard :item="branch" :selected="branch.id === activeBranchId" hide-organization-details />
                      </div>
                    </template>

                    <Alert v-else variant="blue">
                      <LucideNewspaper />
                      <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
                    </Alert>
                  </div>
                </div>
              </SidebarBlock>
            </aside>
          </template>
        </ContentLayout>
      </div>
    </Container>
  </Main>
</template>

<style>
.map-popup .leaflet-popup-content-wrapper {
  @apply rounded-lg;
}

.map-popup .leaflet-popup-content {
  @apply my-1 ml-1 mr-5;
}

.map-popup_body {
  @apply m-0 min-w-[280px] p-0;
}
</style>
